import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'

import { AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { getPossessivePronoun } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  CareProgressData,
  CareProgressSummaryStatus,
  Episode,
  EpisodeStates,
} from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { CLIENT_NOTES } from '../../../common/constants/routingConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useAssignmentsData } from '../../assignments/data/hooks/useAssignmentsData'
import { useGetNotesQuery } from '../../clientNotes/data/notesApi'
import { useSelectedClientData } from '../../clients/clientDetails/data/hooks/useSelectedClientData'
import { useCareConsent } from '../../clients/clientDetails/hooks/useCareConsent'
import { useCareConsentModalContent } from '../../clients/clientDetails/hooks/useCareConsentModalContent'
import { useIntakeForm } from '../../clients/clientDetails/hooks/useIntakeForm'
import { useIntakeModalContent } from '../../clients/clientDetails/hooks/useIntakeFormModalContent'
import { useSelectedEpisodeForClient } from '../../episodes/data/hooks/useSelectedEpisodeForClient'
import { useGetCareProgressSummaryQuery } from '../data/careProgressSummaryApi'

const NO_CARE_PROGRESS_DATA: CareProgressData = {
  cta: { text: '', onPress: () => {}, show: false },
  summary: '',
}

export const useCareProgressSummary = (
  setModalData: React.Dispatch<React.SetStateAction<null | JSX.Element>>,
): CareProgressData => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const userId = useSelector(getAuthUserId)
  const { data: clientDetails } = useSelectedClientData()
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const intakeForm = useIntakeForm()
  const intakeFormStatus = intakeForm?.status
  const intakeFormModalContent = useIntakeModalContent()
  const { isLoading: isLoadingAssignments } = useAssignmentsData()
  const careConsent = useCareConsent()
  const careConsentModalContent = useCareConsentModalContent()
  const { careProgressSummaryUI, isClientHeaderRedesignEnabled } = useFlags()

  const {
    data: careProgressSummaryResponse = { summary: { content: '', status: '' } },
    isLoading: isLoadingCareProgressSummary,
  } = useGetCareProgressSummaryQuery(
    {
      episodeId: selectedEpisode?.id,
      providerId: userId,
    },
    {
      skip: !careProgressSummaryUI || !selectedEpisode?.id || selectedEpisode.state === EpisodeStates.COMPLETED,
      refetchOnMountOrArgChange: true,
    },
  )

  const { data: notes = [], isLoading: isLoadingNotes } = useGetNotesQuery(
    selectedEpisode?.id ? { episodeId: selectedEpisode.id } : skipToken,
  )

  const hasCompletedSessionNote = notes.some(
    (note) =>
      [NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type) && note.status === NoteStatusStates.COMPLETED,
  )

  if (
    selectedEpisode?.state === EpisodeStates.COMPLETED ||
    isLoadingCareProgressSummary ||
    isLoadingNotes ||
    (isClientHeaderRedesignEnabled && isLoadingAssignments) ||
    (!careProgressSummaryUI && intakeFormStatus !== AssignmentStatuses.completed)
  ) {
    return NO_CARE_PROGRESS_DATA
  }

  if (
    isClientHeaderRedesignEnabled &&
    careProgressSummaryUI &&
    selectedEpisode?.state === EpisodeStates.IN_PROGRESS &&
    !hasCompletedSessionNote &&
    careProgressSummaryResponse?.summary?.status !== CareProgressSummaryStatus.COMPLETE &&
    intakeForm &&
    careConsent
  ) {
    const isConsentComplete = careConsent.status === AssignmentStatuses.completed
    const isIntakeComplete = intakeFormStatus === AssignmentStatuses.completed

    if (isIntakeComplete && !isConsentComplete) {
      return NO_CARE_PROGRESS_DATA
    }

    return {
      ...NO_CARE_PROGRESS_DATA,
      careOnboardingStatus: {
        state: !isConsentComplete ? 'bothIncomplete' : isIntakeComplete ? 'bothComplete' : 'consentOnly',
        onViewConsent: () => {
          track({ event: mixpanelEvents.OPEN_CONSENT_FORM_CARE_PROGRESS_SUMMARY })
          setModalData(careConsentModalContent)
        },
        onViewIntake: () => {
          track({ event: mixpanelEvents.OPEN_INTAKE_FORM_CARE_PROGRESS_SUMMARY })
          setModalData(intakeFormModalContent)
        },
      },
    }
  }

  const viewIntakeForm = formatMessage({
    defaultMessage: 'View intake form',
    description: 'Link to show client intake form',
  })
  const viewAllNotes = formatMessage({
    defaultMessage: 'View all notes',
    description: 'Link to notes tab',
  })

  if (careProgressSummaryResponse?.summary?.status === CareProgressSummaryStatus.COMPLETE) {
    return {
      cta: {
        text: viewAllNotes,
        onPress: () => {
          track({ event: mixpanelEvents.OPEN_SESSION_NOTES_CARE_PROGRESS_SUMMARY })
          navigate(CLIENT_NOTES.route)
        },
        show: true,
        testID: 'view-all-notes-cta',
      },
      isAIGenerated: true,
      summary: careProgressSummaryResponse.summary?.content,
    }
  }

  if (
    careProgressSummaryResponse?.summary?.status &&
    [CareProgressSummaryStatus.IN_PROGRESS, CareProgressSummaryStatus.NOT_STARTED].includes(
      careProgressSummaryResponse?.summary?.status,
    )
  ) {
    return {
      cta: {
        text: viewAllNotes,
        onPress: () => {
          track({ event: mixpanelEvents.OPEN_SESSION_NOTES_CARE_PROGRESS_SUMMARY })
          navigate(CLIENT_NOTES.route)
        },
        show: true,
        testID: 'view-all-notes-cta',
      },
      summary: formatMessage(
        {
          defaultMessage: 'Generating {clientFirstName}’s care progress summary, usually takes under 5 minutes...',
          description: 'Generating care progress summary message',
        },
        {
          clientFirstName: clientDetails?.preferred_first_name || clientDetails?.first_name || '',
        },
      ),
    }
  }

  if (careProgressSummaryResponse?.summary?.status === CareProgressSummaryStatus.ERROR) {
    // TODO: handle error message on ICE-5728
    return {
      cta: {
        text: '',
        onPress: () => {},
        show: false,
        testID: 'no-care-progress-summary-cta',
      },
      summary: '',
    }
  }

  const initialNoteHasBeenCompleted = notes.some(
    (note) => note.note_type === NoteTypes.INITIAL && note.status === NoteStatusStates.COMPLETED,
  )

  if (intakeFormStatus === AssignmentStatuses.completed && !initialNoteHasBeenCompleted) {
    return {
      cta: {
        text: viewIntakeForm,
        onPress: () => {
          track({ event: mixpanelEvents.OPEN_INTAKE_FORM_CARE_PROGRESS_SUMMARY })
          setModalData(intakeFormModalContent)
        },
        show: true,
        testID: 'view-intake-form-cta',
      },
      summary: formatMessage(
        {
          defaultMessage: '{clientFirstName} has completed {preferred_pronoun} intake form.',
          description: 'description indicating that client has completed their intake form',
        },
        {
          clientFirstName: clientDetails?.preferred_first_name || clientDetails?.first_name || '',
          preferred_pronoun: getPossessivePronoun(clientDetails?.pronouns),
        },
      ),
    }
  }

  return NO_CARE_PROGRESS_DATA
}
